import React, { useCallback, useRef, useState } from "react";
import { Button, Input, Spin, Tag, Typography, Space } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { productsService } from "../../../../shared/services";
import {
  CategoryConfig,
  ExcludedProduct,
  FreeSlotProduct,
} from "../../hooks/useCreatePromotion";

const { Text } = Typography;
const SEARCH_DEBOUNCE_MS = 400;

interface Props {
  freeSlotProducts: FreeSlotProduct[];
  onChange: (products: FreeSlotProduct[]) => void;
  categories: CategoryConfig[];
  excludedProducts: ExcludedProduct[];
  promoType: "2x1" | "3x2" | "custom";
}

export const PromotionFreeSlotProducts: React.FC<Props> = ({
  freeSlotProducts,
  onChange,
  categories,
  excludedProducts,
  promoType,
}) => {
  const [searchOptions, setSearchOptions] = useState<FreeSlotProduct[]>([]);
  const [fetching, setFetching] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const primaryCategoryIds = categories
    .filter((c) => c.role === "primary" && c.category_id > 0)
    .map((c) => c.category_id);

  const searchProducts = useCallback(
    (searchTerm: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!searchTerm.trim()) {
        setSearchOptions([]);
        return;
      }

      debounceRef.current = setTimeout(() => {
        setFetching(true);

        productsService
          .find({
            query: {
              status: "active",
              data_value: searchTerm.trim(),
              $limit: 30,
              $select: [
                "id",
                "name",
                "category_id",
                "category_id_2",
                "category_id_3",
              ],
              $client: {
                skipJoins: true,
              },
            },
          })
          .then((res: any) => {
            const raw = res?.data ?? res;
            const data = Array.isArray(raw) ? raw : raw?.data ?? [];
            const selectedIds = new Set(freeSlotProducts.map((p) => p.id));
            const excludedIds = new Set(excludedProducts.map((p) => p.id));

            setSearchOptions(
              data
                .filter((p: any) => {
                  if (selectedIds.has(p.id) || excludedIds.has(p.id)) {
                    return false;
                  }
                  if (primaryCategoryIds.length === 0) {
                    return true;
                  }
                  const inPrimary = primaryCategoryIds.some(
                    (catId) =>
                      p.category_id === catId ||
                      p.category_id_2 === catId ||
                      p.category_id_3 === catId,
                  );
                  return !inPrimary;
                })
                .map((p: any) => ({ id: p.id, name: p.name })),
            );
          })
          .catch(() => setSearchOptions([]))
          .finally(() => {
            setFetching(false);
            debounceRef.current = null;
          });
      }, SEARCH_DEBOUNCE_MS);
    },
    [freeSlotProducts, excludedProducts, primaryCategoryIds],
  );

  const handleSearch = (term: string) => {
    setSearchValue(term);
    searchProducts(term);
  };

  const handleAdd = (product: FreeSlotProduct) => {
    if (freeSlotProducts.some((p) => p.id === product.id)) return;
    onChange([...freeSlotProducts, product]);
    setSearchOptions((prev) => prev.filter((p) => p.id !== product.id));
  };

  const handleRemove = (productId: number) => {
    onChange(freeSlotProducts.filter((p) => p.id !== productId));
  };

  if (promoType === "custom") {
    return null;
  }

  const showResults =
    searchValue.trim().length > 0 && (fetching || searchOptions.length > 0);

  return (
    <div>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        {promoType === "3x2"
          ? "Elige qué rollos puede usar el cliente como tercer producto (el que puede salir gratis si es el más barato). Solo se listan productos fuera de la categoría principal."
          : "Productos que pueden completar la promo como ítem adicional (fuera de la categoría principal)."}
        {" "}
        Si no agregas ninguno, la app permitirá todos los productos fuera de la
        categoría principal.
      </Text>

      {primaryCategoryIds.length === 0 && (
        <Text type="warning" style={{ display: "block", marginBottom: 12 }}>
          Primero configura la categoría principal para filtrar correctamente.
        </Text>
      )}

      <Input
        allowClear
        prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
        placeholder="Buscar producto para el slot gratis..."
        value={searchValue}
        onChange={(e) => handleSearch(e.target.value)}
        disabled={primaryCategoryIds.length === 0}
        style={{ marginBottom: 8 }}
      />

      {fetching && (
        <div style={{ textAlign: "center", padding: 12 }}>
          <Spin size="small" />
        </div>
      )}

      {showResults && !fetching && searchOptions.length > 0 && (
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            maxHeight: 220,
            overflowY: "auto",
            marginBottom: 12,
            background: "#fff",
          }}
        >
          {searchOptions.map((p) => (
            <div
              key={p.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "8px 12px",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <Text style={{ flex: 1, fontSize: 13 }}>
                {p.name}{" "}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  (#{p.id})
                </Text>
              </Text>
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => handleAdd(p)}
                style={{ flexShrink: 0 }}
              >
                Agregar
              </Button>
            </div>
          ))}
        </div>
      )}

      {showResults && !fetching && searchOptions.length === 0 && (
        <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 13 }}>
          No hay más resultados para &quot;{searchValue}&quot;
        </Text>
      )}

      {freeSlotProducts.length > 0 ? (
        <Space size={[8, 8]} wrap>
          {freeSlotProducts.map((p) => (
            <Tag
              key={p.id}
              closable
              color="green"
              onClose={() => handleRemove(p.id)}
              style={{ padding: "4px 8px", fontSize: 13 }}
            >
              {p.name}
            </Tag>
          ))}
        </Space>
      ) : (
        <Text type="secondary" style={{ fontSize: 13 }}>
          Sin productos definidos (se usará el catálogo completo fuera de
          premium).
        </Text>
      )}
    </div>
  );
};
